// useMergeConversation — Polish #4 mutation that folds a duplicate source
// thread INTO the current (target) conversation. Called from the
// DuplicateThreadBanner in Command Center.
//
// Endpoint: POST /api/conversations/:target_id/merge  { source_id }
// Response: { ok: true, target_id, source_id, absorbed_newer_message }
//
// After a successful merge we invalidate both conversation caches so the
// Center view re-fetches with the newly-absorbed messages, and the Standup
// / search / customer-history caches drop the ghost source row.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';

interface MergeInput {
  targetId: string;
  sourceId: string;
}

interface MergeResponse {
  ok: true;
  target_id: string;
  source_id: string;
  absorbed_newer_message: boolean;
}

export function useMergeConversation() {
  const qc = useQueryClient();
  return useMutation<MergeResponse, Error, MergeInput>({
    mutationFn: async ({ targetId, sourceId }) => {
      return apiPost<MergeResponse>(
        `/api/conversations/${encodeURIComponent(targetId)}/merge`,
        { source_id: sourceId },
      );
    },
    onSuccess: (_res, vars) => {
      // Target thread now holds the merged-in messages.
      qc.invalidateQueries({ queryKey: ['conversation', vars.targetId] });
      // Customer history no longer includes the source.
      qc.invalidateQueries({ queryKey: ['customer-history'] });
      // Bucket lists hide the source.
      qc.invalidateQueries({ queryKey: ['conversations'] });
      // Search results hide the source.
      qc.invalidateQueries({ queryKey: ['search-conversations'] });
    },
  });
}
